import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST } from '../../../constants/common'
import {
  useAsyncCallback,
  useInlineNotifications,
  useSelection,
  useValidationWithoutFormikNotifications,
} from '../../../hooks'
import { useFilePreviewGallery } from '../../FilesPreviewGallery'
import { TableRowActions } from '../../Table'
import { TableModal, useTableModalCloseHandler } from '../../TableModal'
import { useFileTableLoader } from '../File.hooks'
import { FileAttachedItemsModalTableRow } from '../FileAttachedItemsModalTableRow'
import { FileDirectoryBreadcrumbs } from '../FileDirectoryBreadcrumbs'

import { Actions } from './Actions'
import {
  ATTACHED_FILES_COLUMNS_CONFIG,
  QA_NAME_FILE_ATTACH_MODAL,
} from './FileAttachedItemsModal.constants'
import {
  getAttachedItemsApiParams,
  isEqualFileAttachedItemsModal,
  parseAttachedItemPaginationData,
} from './FileAttachedItemsModal.utils'

import * as styles from './FileAttachedItemsModal.module.css'

import FileIcon from '../../../icons/file.svg'

function FileAttachedItemsModal({
  Counter = FileDirectoryBreadcrumbs,
  confirmationDescription,
  downloadFileDocument,
  entityId,
  filePreviewProps,
  getApiProps = getAttachedItemsApiParams,
  getDataPagination = parseAttachedItemPaginationData,
  getFileById,
  headerProps,
  objectId,
  readonly,
  onClose,
  onSubmit,
  ...restProps
}) {
  const { t } = useTranslation()

  const [isSubmitting, setSubmitting] = useState(false)

  const {
    breadcrumbsItems,
    count,
    dataOrdering,
    files,
    handleClickFolder,
    handleSetOrdering,
    isInitLoading,
    loading,
    pagination,
    setPagination,
  } = useFileTableLoader({
    ...restProps,
    getApiProps,
    getDataPagination,
    objectId: objectId || entityId,
  })

  const { activeFileIndex, handleClickFile, handleCloseFilePreview } =
    useFilePreviewGallery({
      files,
    })

  const {
    checkIsItemSelected,
    exclude,
    handleSelect,
    handleSelectAll,
    handleUnselectAllPages,
    isAllSelected,
    isRemoveSelection,
    selectAllPages,
    selected,
    selectedIds,
  } = useSelection({ data: { items: files }, pagination })

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const isDeletingState = selectedIds.length > 0 && !readonly

  const handleSubmit = useAsyncCallback(() => {
    setSubmitting(true)

    return onSubmit({ files, id: entityId, removedFiles: selectedIds }).catch(
      () => {
        setSubmitting(false)
      }
    )
  }, [entityId, files, onSubmit, selectedIds])

  const { handleCloseTableModal, isCloseConfirm, setCloseConfirm } =
    useTableModalCloseHandler({
      initialSelectionValues: EMPTY_LIST,
      selected,
      onClose,
    })

  const { isValidForm, setNotificationListRef } =
    useValidationWithoutFormikNotifications({
      validationSchemaProps: {
        isEqual: isEqualFileAttachedItemsModal,
        isEqualProps: { isDeletingState, readonly },
      },
      onRemoveNotification: handleRemoveNotification,
      onSetNotification: handleAddNotification,
    })

  function handleSubmitFilesModal() {
    if (!isValidForm()) {
      return
    }

    if (isDeletingState) {
      setCloseConfirm(true)
    } else {
      onClose()
    }
  }

  function handleApplyConfirmation() {
    return isDeletingState ? handleSubmit() : onClose()
  }

  const confirmationModalProps = isDeletingState
    ? {
        confirmationApplyText: t('Modal.ConfirmationDeleteBtnKey'),
        confirmationCancelText: t('Modal.CancelBtnKey'),
        confirmationDescription,
        confirmationTitleText: t('Modal.ConfirmationDeleteTitleKey'),
        onApplyConfirmModal: handleApplyConfirmation,
      }
    : null

  return (
    <TableModal
      {...confirmationModalProps}
      initialFullScreen
      Actions={Actions}
      Counter={Counter}
      Search={null}
      actionsProps={{
        buttonCancelProps: {
          onClick: handleCloseTableModal,
        },
        buttonConfirmProps: {
          disabled: !selected,
          isLoading: isSubmitting,
          onClick: onClose,
        },
        buttonRemoveProps: {
          onClick: handleSubmitFilesModal,
        },
        ordering: dataOrdering,
        readonly,
      }}
      contentProps={{ loading }}
      counterProps={{
        count,
        items: breadcrumbsItems,
      }}
      emptyStateProps={{
        EntityIcon: FileIcon,
        text: t('Modal.AttachedFilesNoDataKey'),
      }}
      filePreviewProps={{
        ...filePreviewProps,
        downloadFileDocument,
        files,
        getFileById,
        initActive: activeFileIndex,
        onClose: handleCloseFilePreview,
      }}
      headerProps={{
        ...headerProps,
        text: headerProps?.text || 'Modal.AttachedFilesTitleKey',
        onClose: onClose,
      }}
      isCloseConfirm={isCloseConfirm}
      isInitLoading={isInitLoading}
      loading={loading}
      notificationListProps={{
        ref: setNotificationListRef,
      }}
      notifications={notifications}
      paginationProps={{ pagination, onChange: setPagination }}
      qaName={QA_NAME_FILE_ATTACH_MODAL}
      selectAllPages={selectAllPages}
      selected={selected}
      selectionToolbarProps={{
        exclude,
        pagination: { count },
        selectAllPages,
        selected,
        onClose: handleUnselectAllPages,
      }}
      setCloseConfirm={setCloseConfirm}
      tableItems={files}
      tableProps={{
        TableRow: FileAttachedItemsModalTableRow,
        checkIsItemSelected,
        className: styles.table,
        columns: ATTACHED_FILES_COLUMNS_CONFIG,
        headerRowProps: {
          Actions: TableRowActions,
          actionsProps: {
            checked: isAllSelected,
            disabled: readonly,
            ordering: dataOrdering,
            selected: isRemoveSelection,
            onChange: handleSelectAll,
          },
          ordering: dataOrdering,
          resizable: false,
          withCellBorder: false,
          onSetOrdering: handleSetOrdering,
        },
        items: files,
        rowProps: {
          ...(readonly ? { Actions: null } : null),
          disabled: readonly,
          onClick: handleClickFile,
          onClickFolder: handleClickFolder,
          onSelect: handleSelect,
        },
      }}
      onClose={onClose}
      onTableModalClose={handleCloseTableModal}
    />
  )
}

export default FileAttachedItemsModal
